import { Breakpoints, Container, ContainerPadding, CTA } from '@volkswagen-onehub/components-core';
import { Close } from '@volkswagen-onehub/icons-core';
import { FocusLayerSizeV2, ShimBackgroundTypeV2 } from '@volkswagen-onehub/layer-manager';
import React from 'react';
import { PortalContext } from '../../../../context/portal-context';
import { useVouchersTracker } from '../hooks/use-vouchers-tracker';
import { VouchersPopup } from './vouchers-popup';
import { VouchersvwBank } from './vouchers-vwbank';
import { GetPersonUser, NormalizedVoucher } from '@esportal/portal-service';

export const VouchersPopupLayer: React.FC<{
	className: string;
	vouchersPopupData: NormalizedVoucher | undefined;
	isVouchersPopupVisible: boolean;
	setIsVouchersPopupVisible: React.Dispatch<React.SetStateAction<boolean>>;
	vouchersLayerRef: React.RefObject<HTMLElement>;
	userData: GetPersonUser | null;
}> = ({ vouchersPopupData, setIsVouchersPopupVisible, isVouchersPopupVisible, vouchersLayerRef, userData }) => {
	const { layerManager } = React.useContext(PortalContext);
	const portalContext = React.useContext(PortalContext);
	const trackVWFSLayerCloseClickRef = React.useRef<(() => void) | null>(null);
	const [vwBankLayerRef, setvwBankLayerRef] = React.useState<any>(null);

	const handleClose = () => {
		setIsVouchersPopupVisible(false);

		if (vouchersLayerRef?.current) {
			(vouchersLayerRef?.current as HTMLDialogElement).close();
			return ((vouchersLayerRef.current as any) = null);
		}
		return null;
	};

	const closevwBankLayer = () => {
		trackVWFSLayerCloseClickRef.current?.();
		if (vwBankLayerRef) {
			vwBankLayerRef.close();
			setvwBankLayerRef(null);
		}
	};

	const vwBankLayer = () => {
		const vwBankUrl = vouchersPopupData?.layer?.cta?.src;

		const layer = layerManager.openContentLayerV2(
			() => {
				return (
					<PortalContext.Provider value={portalContext}>
						{vwBankUrl ? (
							<VouchersvwBank vwBankUrl={vwBankUrl} userData={userData} voucher={vouchersPopupData} />
						) : (
							<></>
						)}
					</PortalContext.Provider>
				);
			},
			{},
			{
				shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
				closeButtonLabel: '',
				contentHeadline: '',
				onClose: closevwBankLayer,
			},
		);
		setvwBankLayerRef(layer);
	};

	const renderVouchersPopUpLayer = () => {
		return (
			<PortalContext.Provider value={portalContext}>
				{vouchersPopupData && (
					<VouchersPopupLayerContent
						voucher={vouchersPopupData}
						onClose={handleClose}
						vwBankLayer={vwBankLayer}
						onTrackVWFSLayerClose={(trackClose) => {
							trackVWFSLayerCloseClickRef.current = trackClose;
						}}
					/>
				)}
			</PortalContext.Provider>
		);
	};

	React.useEffect(() => {
		let layer: any;
		if (isVouchersPopupVisible) {
			layer = layerManager.openFocusLayer(
				renderVouchersPopUpLayer,
				{},
				{
					size: FocusLayerSizeV2.A,
					shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
				},
			);
			(vouchersLayerRef.current as any) = layer;
		}
		return () => {
			if (layer) {
				layer.close();
				(vouchersLayerRef.current as any) = null;
			}
		};
	}, [isVouchersPopupVisible, layerManager, vouchersLayerRef, portalContext]);

	return null;
};

const VouchersPopupLayerContent: React.FC<{
	voucher: NormalizedVoucher;
	onClose: () => void;
	vwBankLayer: () => void;
	onTrackVWFSLayerClose: (trackClose: () => void) => void;
}> = ({ voucher, onClose, vwBankLayer, onTrackVWFSLayerClose }) => {
	const { trackLayerCloseClick, trackVWFSLayerCloseClick } = useVouchersTracker(voucher);

	React.useEffect(() => {
		onTrackVWFSLayerClose(trackVWFSLayerCloseClick);
	}, [onTrackVWFSLayerClose, trackVWFSLayerCloseClick]);

	return (
		<>
			<Container
				horizontalAlign={'flex-end'}
				padding={{
					top: ContainerPadding.dynamic0100,
					right: {
						[Breakpoints.default]: ContainerPadding.grid001,
						[Breakpoints.b1600]: ContainerPadding.dynamic0100,
					},
					bottom: ContainerPadding.dynamic0100,
				}}
			>
				<CTA
					tag="button"
					emphasis="tertiary"
					onClick={() => {
						trackLayerCloseClick();
						onClose();
					}}
					aria-label="Close pop-up"
					icon={<Close variant="default" aria-hidden />}
				/>
			</Container>
			{voucher.layer && <VouchersPopup vouchersPopupData={voucher} vwBankLayer={vwBankLayer} />}
		</>
	);
};
